import React, { useState } from 'react';
import { Table, Button, Space, Tag, Tooltip, message } from 'antd';
import {
  AiOutlineEdit,
  AiOutlineDelete,
  AiOutlineLoading,
  AiOutlineSync,
  AiOutlineApi,
  AiOutlineCheckCircle,
  AiOutlineCloseCircle,
  AiOutlineReload,
} from 'react-icons/ai';
import { Link } from 'react-router-dom';
import BiometricDeviceModal from '../../../../components/modelpopup/BiometricDeviceModal';
import {
  useGetBiometricDevicesWithStatusQuery,
  useTestDeviceConnectionMutation,
  useSyncDeviceAttendanceMutation,
  useSyncAllDevicesAttendanceMutation,
} from '../../../../services/biometric.service';
import { useDispatch } from 'react-redux';
import { toggleDelete } from '../../../../redux/slices/ui.slice';

const BiometricDevices = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [testingDevice, setTestingDevice] = useState(null);
  const [syncingDevice, setSyncingDevice] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const dispatch = useDispatch();

  // API Hooks - use the with-status endpoint for real-time connection status
  const { data: devicesData, isLoading, refetch, isFetching } = useGetBiometricDevicesWithStatusQuery(
    undefined,
    { pollingInterval: 30000 } // Poll every 30 seconds for status updates
  );
  const [testConnection] = useTestDeviceConnectionMutation();
  const [syncAttendance] = useSyncDeviceAttendanceMutation();
  const [syncAllDevices] = useSyncAllDevicesAttendanceMutation();

  const devices = devicesData?.data || [];

  // Device Status Colors
  const getStatusColor = (status) => {
    const colors = {
      online: 'success',
      offline: 'default',
      error: 'error',
      syncing: 'processing',
    };
    return colors[status] || 'default';
  };

  // Device Type Badge
  const getDeviceTypeBadge = (type) => {
    const badges = {
      zkteco: { color: 'blue', text: 'ZKTeco' },
      realand: { color: 'purple', text: 'Realand' },
    };
    const badge = badges[type] || { color: 'default', text: type };
    return <Tag color={badge.color}>{badge.text}</Tag>;
  };

  // Handle Add Device
  const handleAdd = () => {
    setEditingDevice(null);
    setModalVisible(true);
  };

  // Handle Edit Device
  const handleEdit = (device) => {
    setEditingDevice(device);
    setModalVisible(true);
  };

  // Handle Delete Device
  const doDelete = (e, item) => {
    dispatch(
      toggleDelete({
        open: true,
        title: 'Biometric Device',
        url: `/biometric-devices/${item.id}`,
        ele: e.currentTarget,
        parent: 'tr'
      })
    );
  };

  // Handle Test Connection
  const handleTestConnection = async (device) => {
    setTestingDevice(device.id);
    try {
      const result = await testConnection(device.id).unwrap();
      if (result.success) {
        message.success(`${device.device_name} is online and connected!`);
        refetch();
      } else {
        message.error(`Failed to connect to ${device.device_name}`);
      }
    } catch (error) {
      message.error(error?.data?.message || 'Connection test failed');
    } finally {
      setTestingDevice(null);
    }
  };

  // Handle Sync Attendance
  const handleSync = async (device) => {
    setSyncingDevice(device.id);
    try {
      const result = await syncAttendance({ deviceId: device.id }).unwrap();
      message.success(`Synced ${result.data?.newRecords || 0} attendance records from ${device.device_name}`);
      refetch();
    } catch (error) {
      message.error(error?.data?.message || 'Sync failed');
    } finally {
      setSyncingDevice(null);
    }
  };

  // Handle manual refresh
  const handleRefresh = () => {
    refetch();
    message.info('Refreshing device status...');
  };

  // Handle Sync All Devices
  const handleSyncAll = async () => {
    setSyncingAll(true);
    message.loading({ content: 'Syncing all devices...', key: 'syncAll', duration: 0 });
    try {
      const result = await syncAllDevices().unwrap();
      if (result.success) {
        const { successCount, failedCount, totalRecords } = result.data || {};
        message.success({
          content: `Sync complete: ${successCount} succeeded, ${failedCount} failed, ${totalRecords} new records`,
          key: 'syncAll',
          duration: 5,
        });
        refetch();
      } else {
        message.error({ content: result.message || 'Sync failed', key: 'syncAll' });
      }
    } catch (error) {
      message.error({ content: error?.data?.message || 'Sync all devices failed', key: 'syncAll' });
    } finally {
      setSyncingAll(false);
    }
  };

  // Format relative time
  const formatRelativeTime = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return then.toLocaleDateString();
  };

  // Table Columns
  const columns = [
    {
      title: 'Device Name',
      dataIndex: 'device_name',
      key: 'device_name',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          {record.location && (
            <div style={{ fontSize: '12px', color: '#888' }}>{record.location}</div>
          )}
          {record.serial_number && (
            <div style={{ fontSize: '11px', color: '#aaa' }}>SN: {record.serial_number}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Branch',
      dataIndex: 'branch',
      key: 'branch',
      render: (branch) => branch?.name || '-',
    },
    {
      title: 'Type',
      dataIndex: 'device_type',
      key: 'device_type',
      render: (type) => getDeviceTypeBadge(type),
    },
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address',
      render: (ip, record) => `${ip}:${record.port}`,
    },
    {
      title: 'State',
      key: 'connection_status',
      width: 100,
      render: (_, record) => {
        const isOnline = record.is_online;
        return (
          <Tag
            color={isOnline ? 'success' : 'default'}
            icon={isOnline ? <AiOutlineCheckCircle /> : <AiOutlineCloseCircle />}
          >
            {isOnline ? 'Online' : 'Offline'}
          </Tag>
        );
      },
    },
    {
      title: 'Last Activity',
      dataIndex: 'last_connected_at',
      key: 'last_connected_at',
      render: (date) => (
        <Tooltip title={date ? new Date(date).toLocaleString() : 'Never connected'}>
          <span>{formatRelativeTime(date)}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Users',
      dataIndex: 'registered_users_count',
      key: 'registered_users_count',
      width: 80,
      render: (count, record) => (
        <Tooltip title={`Capacity: ${record.user_capacity || 'N/A'}`}>
          <span>{count || 0}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Last Sync',
      dataIndex: 'last_sync_at',
      key: 'last_sync_at',
      render: (date) => (
        <Tooltip title={date ? new Date(date).toLocaleString() : 'Never synced'}>
          <span>{formatRelativeTime(date)}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Test Connection">
            <Button
              type="text"
              size="small"
              icon={testingDevice === record.id ? <AiOutlineLoading /> : <AiOutlineApi />}
              onClick={() => handleTestConnection(record)}
              disabled={testingDevice === record.id}
            />
          </Tooltip>
          <Tooltip title="Sync Attendance">
            <Button
              type="text"
              size="small"
              icon={syncingDevice === record.id ? <AiOutlineLoading /> : <AiOutlineSync />}
              onClick={() => handleSync(record)}
              disabled={syncingDevice === record.id}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<AiOutlineEdit />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              size="small"
              danger
              icon={<AiOutlineDelete />}
              onClick={(e) => doDelete(e, record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Breadcrumb */}
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">Biometric Devices</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/employee-dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Settings</li>
                </ul>
              </div>
              
              <div className="col-auto float-end ms-auto">
                <Button
                  type="default"
                  icon={isFetching ? <AiOutlineLoading /> : <AiOutlineReload />}
                  onClick={handleRefresh}
                  disabled={isFetching}
                  style={{ marginRight: 8 }}
                >
                  Refresh
                </Button>
                <Button
                  type="primary"
                  icon={syncingAll ? <AiOutlineLoading /> : <AiOutlineSync />}
                  onClick={handleSyncAll}
                  disabled={syncingAll || devices.length === 0}
                  style={{ marginRight: 8 }}
                >
                  Sync All Devices
                </Button>
                <Link
                  to="#"
                  className="btn add-btn"
                  onClick={handleAdd}
                >
                  <i className="fa fa-plus" />
                  Add Device
                </Link>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="card mb-0">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col">
                      <h4 className="card-title mb-0">Biometric Devices</h4>
                      <p className="text-muted mb-0">
                        Manage attendance tracking devices (ZKTeco, Realand)
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  <Table
                    columns={columns}
                    dataSource={devices}
                    rowKey="id"
                    loading={isLoading}
                    scroll={{ x: 1200 }}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showTotal: (total) => `Total ${total} devices`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <BiometricDeviceModal
        visible={modalVisible}
        device={editingDevice}
        onClose={() => {
          setModalVisible(false);
          setEditingDevice(null);
        }}
        onSuccess={() => {
          setModalVisible(false);
          setEditingDevice(null);
          refetch();
        }}
      />
    </>
  );
};

export default BiometricDevices;
